<div class="task">
  <section class="bg-white rounded-xl p-4">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-xl font-bold text-[#151D48]">
        {{ 'TASKS.TITLE' | translate }}
      </h2>

      <button
        type="button"
        class="bg-secondary text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
        (click)="loadTasks()"
        [disabled]="loading"
      >
        {{ 'COMMON.REFRESH' | translate }}
      </button>
    </div>

    <p class="text-sm text-slate-500 mb-3">
      {{ 'TASKS.TASK_LIST' | translate }}
    </p>

    @if (loading) {
      <p class="text-sm text-slate-500">
        {{ 'TASKS.LOADING_TASKS' | translate }}
      </p>
    }

    @if (error) {
      <p class="text-sm text-red-600 mb-3">
        {{ error | translate }}
      </p>
    }

    <app-table
      [columns]="taskColumns"
      [data]="tasks"
      [actions]="taskActions"
      [selectable]="false"
      [enablePagination]="true"
      [pageSize]="10"
    >
    </app-table>
  </section>
</div>

<!-- Approve / Reject / Send Back confirmation -->
<app-popup
  [title]="popupTitle"
  type="custom"
  width="w-full max-w-4xl"
[(show)]="showConfirmPopup"
>
  <div class="space-y-6">
    <!-- Confirmation message -->
    <p class="text-sm text-gray-600">
      {{ popupMessage | translate: popupMessageParams }}
    </p>

    <!-- New comment -->
    <div class="border rounded-2xl p-5 bg-gray-50">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-semibold text-lg text-[#0B0D5C]">
          {{ 'TASKS.ADD_COMMENT' | translate }}
          <span class="text-red-500 text-sm font-normal">
            *
          </span>
        </h3>

        <span class="text-sm text-gray-400">
          {{ processComment.length }}/5000
        </span>
      </div>

      <textarea
        [(ngModel)]="processComment"
        rows="5"
        maxlength="5000"
        [placeholder]="'TASKS.WRITE_COMMENT_PLACEHOLDER' | translate"
        class="w-full border border-gray-200 rounded-xl p-4 bg-white outline-none resize-none focus:border-[#1E2A78] focus:ring-1 focus:ring-[#1E2A78]"
      ></textarea>

      <p class="text-sm text-gray-400 mt-3">
        {{ 'TASKS.COMMENT_SUBMIT_HINT' | translate }}
      </p>
    </div>

    <!-- Existing comments -->
    <div>
      <div class="flex items-center justify-between mb-4">
        <h3 class="font-semibold text-lg text-[#0B0D5C]">
          {{ 'TASKS.EXISTING_COMMENTS' | translate }}
        </h3>

        <span
          class="bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full"
        >
          {{ taskCommentHistory.length }}
        </span>
      </div>

      <!-- Loading state -->
      @if (isCommentHistoryLoading) {
        <div
          class="border-2 border-dashed border-gray-200 rounded-2xl p-10 text-center text-gray-400"
        >
          <p class="text-sm font-medium">
            {{ 'TASKS.LOADING_COMMENTS' | translate }}
          </p>
        </div>
      }

      <!-- Empty state -->
      @if (!isCommentHistoryLoading && taskCommentHistory.length === 0) {
        <div
          class="border-2 border-dashed border-gray-200 rounded-2xl p-10 text-center text-gray-400"
        >
          <div class="text-base font-medium mb-1">
            {{ 'TASKS.NO_PREVIOUS_COMMENTS' | translate }}
          </div>

          <p class="text-sm">
            {{ 'TASKS.NO_COMMENTS_YET' | translate }}
          </p>
        </div>
      }

      <!-- Existing comments list -->
      @if (!isCommentHistoryLoading && taskCommentHistory.length > 0) {
        <div
          class="max-h-[320px] overflow-y-auto space-y-4 pe-2"
        >
          @for (item of taskCommentHistory; track item) {
            <div
              class="border border-gray-200 rounded-2xl p-4 bg-gray-50"
            >
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3"
              >
                <div class="font-semibold text-[#0B0D5C]">
                  {{
                    item.crtdBy ||
                    item.lastUpdBy ||
                    item.userName ||
                    item.usrNm ||
                    ('TASKS.USER' | translate)
                  }}
                </div>

                <div class="text-xs text-gray-400">
                  <span dir="ltr">{{
                    (item.crtdDt || item.lastUpdDt)
                      | date: 'medium'
                  }}</span>
                </div>
              </div>

              <p class="text-sm text-gray-700 whitespace-pre-wrap break-words">
                {{
                  item.cmmnt ||
                  item.cmnt ||
                  item.comment ||
                  item.dscr ||
                  '-'
                }}
              </p>
            </div>
          }
        </div>
      }
    </div>

    <!-- Confirmation buttons -->
    <div
      class="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2"
    >
      <button
        type="button"
        class="btn-danger"
        [disabled]="isProcessing"
        (click)="closeConfirmPopup()"
      >
        {{ cancelText | translate }}
      </button>

      <button
        type="button"
        class="btn-primary"
        [disabled]="isProcessing"
        (click)="confirmProcessTask()"
      >
        @if (!isProcessing) {
          <span>
            {{ confirmText | translate }}
          </span>
        }

        @if (isProcessing) {
          <span>
            {{ 'TASKS.PROCESSING' | translate }}
          </span>
        }
      </button>
    </div>
  </div>
</app-popup>

<!-- Reject action options -->
<app-popup
  title="TASKS.REJECT_TASK"
  type="custom"
  width="w-full max-w-md"
  [show]="showRejectActionPopup"
  (showChange)="showRejectActionPopup = $event"
>
  <div class="space-y-5">
    <p class="text-sm text-gray-600">
      {{ 'TASKS.SELECT_ACTION' | translate }}
    </p>

    <!-- Non-RO users only -->
    @if (!isRelationshipOfficer) {
      <label
        class="flex items-start gap-3 p-4 border rounded-xl cursor-pointer"
        [ngClass]="
          selectedRejectAction === 'sendBackToRO'
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200'
        "
      >
        <input
          type="radio"
          name="taskRejectAction"
          value="sendBackToRO"
          [(ngModel)]="selectedRejectAction"
          class="mt-1"
        />

        <div>
          <p class="font-semibold text-gray-800">
            {{ sendBackLabel | translate }}
          </p>

          <p class="mt-1 text-sm text-gray-500">
            {{ sendBackHint | translate }}
          </p>
        </div>
      </label>
    }

    <!-- All users, including RO -->
    <label
      class="flex items-start gap-3 p-4 border rounded-xl cursor-pointer"
      [ngClass]="
        selectedRejectAction === 'permanentReject'
          ? 'border-red-500 bg-red-50'
          : 'border-gray-200'
      "
    >
      <input
        type="radio"
        name="taskRejectAction"
        value="permanentReject"
        [(ngModel)]="selectedRejectAction"
        class="mt-1"
      />

      <div>
        <p class="font-semibold text-red-700">
          {{ 'TASKS.PERMANENT_REJECT' | translate }}
        </p>

        <p class="mt-1 text-sm text-gray-500">
          {{ 'TASKS.PERMANENT_REJECT_HINT' | translate }}
        </p>
      </div>
    </label>

    <div class="flex justify-end gap-3 pt-3">
      <button
        type="button"
        class="btn-primary"
        [disabled]="isProcessing"
        (click)="prepareRejectConfirmation()"
      >
        {{ 'COMMON.CONTINUE' | translate }}
      </button>

      <button
        type="button"
        class="btn-danger"
        [disabled]="isProcessing"
        (click)="closeRejectActionPopup()"
      >
        {{ 'COMMON.CANCEL' | translate }}
      </button>
    </div>
  </div>
</app-popup>
<!-- Facility Offer Letter documents (read-only) -->
<app-popup
  title="TASKS.DOCUMENTS"
  type="custom"
  width="w-full max-w-5xl"
  [show]="showDocsPopup"
  (showChange)="closeDocsPopup()"
>
  @if (docsTask) {
    <div class="space-y-4">
      <p class="text-sm text-gray-600">
        {{ docsTask.entityTypeLabel }} — {{ docsTask.description }}
      </p>

      <app-document
        [entitySeq]="docsTask.entySeq"
        [moduleSeq]="docsTask.entityType"
        [viewOnly]="true"
        [inPopup]="true"
      >
      </app-document>
    </div>
  }
</app-popup>
